import { useState } from "react";
import { X, AlertTriangle } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";

/**
 * Open-dispute modal. Used by user + provider dashboards.
 *
 * Props:
 * - booking: { id, service_title, status, ... }
 * - onClose(opened: boolean): closes; opened=true => parent should reload
 */
export default function OpenDisputeModal({ booking, onClose }) {
  const { t } = useI18n();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!booking) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/bookings/${booking.id}/open-dispute`, { reason: reason.trim() });
      onClose(true);
    } catch (e) {
      setError(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-0 md:p-6 bg-ink-deep/70 backdrop-blur-sm"
      data-testid="open-dispute-modal"
      onClick={() => !busy && onClose(false)}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full md:max-w-lg max-h-[95svh] overflow-y-auto modal-panel p-6 space-y-4"
        data-testid="open-dispute-form"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="eyebrow flex items-center gap-2 text-rose-300">
              <AlertTriangle size={12} /> {t("dispute.open_chip", "In dispute")}
            </div>
            <h3 className="font-serif-display text-2xl mt-1">{t("dispute.open_title", "Open a dispute")}</h3>
            <p className="text-sm text-muted mt-2 max-w-md">
              {t("dispute.open_subtitle", "Tell us what went wrong. Our concierge will review within 24 hours and decide on a refund or release.")}
            </p>
          </div>
          <button type="button" onClick={() => onClose(false)} className="text-muted hover:text-bone" aria-label="Close" data-testid="dispute-close">
            <X size={20} />
          </button>
        </div>

        <div className="text-xs text-muted font-mono border-t hairline pt-3">
          #{booking.id.slice(0, 8)} · {booking.service_title}
        </div>

        <label className="block">
          <span className="label-tiny block mb-1.5">{t("dispute.reason_label", "What happened?")}</span>
          <textarea
            required
            minLength={5}
            rows={5}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("dispute.reason_placeholder", "Describe what went wrong — service didn't match listing, no-show, damages, etc.")}
            className="input-bare resize-y"
            data-testid="dispute-reason"
          />
        </label>

        {error && <div className="text-rose-300 text-sm" data-testid="dispute-error">{error}</div>}

        <div className="flex flex-wrap items-center justify-end gap-3">
          <button type="button" onClick={() => onClose(false)} className="btn btn-ghost" data-testid="dispute-cancel">
            {t("dispute.cancel_btn", "Cancel")}
          </button>
          <button type="submit" disabled={busy || reason.trim().length < 5} className="btn btn-primary" data-testid="dispute-submit">
            {busy ? t("dispute.submitting", "Submitting…") : t("dispute.submit_btn", "Submit dispute")}
          </button>
        </div>
      </form>
    </div>
  );
}
